import { useEffect, useMemo, useRef, useState } from 'react';

import { notifications } from '@mantine/notifications';
import dayjs from 'dayjs';
import { useRecoilState, useResetRecoilState, useSetRecoilState } from 'recoil';
import useSWR, { preload, useSWRConfig } from 'swr';

import { createTask } from '../api';
import { getCalculatedAsBuiltDataFiles } from '../api/getCalculatedAsBuiltDataFile';
import { getSchedules } from '../api/getSchedules';
import { convertScheduleConstructionZonesToGanttTask } from '../lib/convertConstructionZonesToGanttTask';
import {
  convertFrappeGanttCrossToGanttSelection,
  convertFrappeGanttDateToGanttSelection,
} from '../lib/ganttSelection/convertFrappeGanttToGanttSelection';
import { convertGanttSelectionToFrappeGantt } from '../lib/ganttSelection/convertGanttSelectionToFrappeGantt';
import { getSelectedTasksWithSoil } from '../lib/ganttSelection/getSelectedTasksWithSoil';
import {
  GanttSelectionType,
  transformGanttSelection,
} from '../lib/ganttSelection/transformGanttSelection';
import { getAllTasks } from '../lib/getAllTasks';
import {
  frappeGanttInstanceState,
  frappeGanttViewModeState,
  ganttSelectionState,
  isGanttInitialRenderState,
  openConstructionZoneIdsState,
  scheduleSizeState,
} from '../store/atom';

import {
  getDisplayedSoilAmountByDate,
  getDisplayedSoilAmountByTask,
} from '@/features/3d/api/getDisplayedSoilAmount';
import { getUser } from '@/features/auth/api/getUser';
import { useAuthorizeRouter } from '@/features/auth/hooks/useAuthorizeRouter';
import { getConstructionZones } from '@/features/saveSite/api/getConstructionZones';
import { getSite } from '@/features/saveSite/api/getSite';
import { TaskFormSchemaType } from '@/features/saveSite/components/ConstructionZone/SaveTaskModal';
import { getDataFiles } from '@/features/siteTop/api/getDataFiles';
import { displayedDataFileState, optionSettingState } from '@/features/siteTop/store/atom';
import { SWR_KEYS } from '@/utils/constant';

const fetchSoilAmountDataByDate = async ([key, dataFileId, meshSize, siteId, ganttSelectionType]: [
  string,
  number | undefined,
  number,
  number | undefined,
  GanttSelectionType | undefined
]) => {
  if (ganttSelectionType !== GanttSelectionType.date) return null;

  //paramsが不足している場合はnullを返却
  if (!siteId || !dataFileId || !meshSize) return null;
  const params = {
    siteId,
    dataFileId,
    meshSize,
  };
  //TODO 前後の日付を、preloadで取得する
  /*preload([
    SWR_KEYS.SOIL_AMOUNT_TASKS_BY_DATE,
    //データファイルID
    dataFileId,
    //メッシュサイズ
    meshSize,
    siteId,
    GanttSelectionType.date,
  ] , fetchSoilAmountDataByDate);*/
  return getDisplayedSoilAmountByDate(params);
};
const fetchSoilAmountDataByTask = async ([
  key,
  taskId,
  dataFileId,
  meshSize,
  siteId,
  ganttSelectionType,
]: [
  string,
  number | undefined,
  number | undefined,
  number,
  number | undefined,
  GanttSelectionType | undefined
]) => {
  if (ganttSelectionType !== GanttSelectionType.cross) return null;

  //paramsが不足している場合はnullを返却
  if (!siteId || !dataFileId || !meshSize || !taskId) return null;
  const params = {
    siteId,
    dataFileId,
    meshSize,
    taskId,
  };

  return getDisplayedSoilAmountByTask(params);
};

export const useSchedule = () => {
  const router = useAuthorizeRouter();
  const { siteId } = router.query;
  const [ganttSelection, setGanttSelectionState] = useRecoilState(ganttSelectionState);
  const [scheduleSize, setScheduleSize] = useRecoilState(scheduleSizeState);
  const [frappeGanttInstance, setFrappeGanttInstance] = useRecoilState(frappeGanttInstanceState);
  const resetFrappeGanttInstance = useResetRecoilState(frappeGanttInstanceState);
  const resetGanttSelectionState = useResetRecoilState(ganttSelectionState);
  const resetScheduleSizeState = useResetRecoilState(scheduleSizeState);
  const resetFrappeGanttViewModeState = useResetRecoilState(frappeGanttViewModeState);
  const resetIsGanttInitialRenderState = useResetRecoilState(isGanttInitialRenderState);
  const [frappeGanttViewMode, setFrappeGanttViewMode] = useRecoilState(frappeGanttViewModeState);
  const [isGanttInitialRender, setIsGanttInitialRenderState] =
    useRecoilState(isGanttInitialRenderState);
  const [openConstructionZoneIds, setOpenConstructionZoneIds] = useRecoilState(
    openConstructionZoneIdsState
  );
  const { data: site } = useSWR([SWR_KEYS.SITE, siteId], () =>
    siteId ? getSite({ siteId }) : null
  );
  const { data: constructionZones = [] } = useSWR(SWR_KEYS.CONSTRUCTION_ZONE_LIST, () =>
    siteId ? getConstructionZones({ siteId }) : []
  );
  const { data: dataFiles = [] } = useSWR(SWR_KEYS.DATA_FILE_LIST, () =>
    siteId ? getDataFiles({ siteId }) : []
  );
  const { data: user } = useSWR(SWR_KEYS.USER, () => getUser());
  const loadSchedule = (opt?: { measurementDate?: string; baseDate?: string }) =>
    siteId ? getSchedules(siteId, opt) : null;

  const {
    data: schedules,
    isValidating: isValidationSchedules,
    isLoading: isSchedulesLoading,
    error,
  } = useSWR([SWR_KEYS.SCHEDULE, siteId], () => loadSchedule(), {
    //スケジュールバーが定期的に更新されるのを防ぐ
    revalidateOnFocus: false,
  });

  const { data: asBuiltDataFiles = [] } = useSWR(
    [SWR_KEYS.CALCULATED_ASBUILT_DATA_FILE_LIST, siteId],
    () => (siteId ? getCalculatedAsBuiltDataFiles(siteId) : [])
  );

  const measurementDate = ganttSelection?.date?.dataFile.measurementDate;
  const { data: schedulesWithQuery, isValidating: isValidatingSchedulesWithQuery } = useSWR(
    [SWR_KEYS.SCHEDULE_WITH_QUERY, siteId, ganttSelection?.date?.dataFile.measurementDate],
    () =>
      loadSchedule({
        measurementDate,
        //任意の日付が選択されている場合は、その日付をbaseDateとして設定する
        baseDate: ganttSelection?.date && ganttSelection?.date?.dataFile.measurementDate,
      }),
    {
      //スケジュールバーが定期的に更新されるのを防ぐ
      revalidateOnFocus: false,
      keepPreviousData: true,
    }
  );
  useEffect(() => {
    //前後のmeasurementDateに対して、preloadし、描画を高速化する
    if (measurementDate) {
      const asBuiltDataFilesOrderByDate = asBuiltDataFiles.sort((a, b) => {
        //日付がない場合は、最後に表示
        if (!a.measurementDate) return 1;
        if (!b.measurementDate) return -1;
        const dateA = dayjs(a.measurementDate);
        const dateB = dayjs(b.measurementDate);
        return dateA.isBefore(dateB) ? -1 : 1;
      });
      const prevMeasurementDate = asBuiltDataFilesOrderByDate.find((asBuiltDataFile) =>
        dayjs(asBuiltDataFile.measurementDate).isBefore(dayjs(measurementDate))
      );
      const nextMeasurementDate = asBuiltDataFilesOrderByDate.find((asBuiltDataFile) =>
        dayjs(asBuiltDataFile.measurementDate).isAfter(dayjs(measurementDate))
      );
      //preload
      preload([SWR_KEYS.SCHEDULE_WITH_QUERY, siteId, prevMeasurementDate?.measurementDate], () =>
        loadSchedule({
          measurementDate: prevMeasurementDate?.measurementDate,
          baseDate: prevMeasurementDate?.measurementDate,
        })
      );
      preload([SWR_KEYS.SCHEDULE_WITH_QUERY, siteId, nextMeasurementDate?.measurementDate], () =>
        loadSchedule({
          measurementDate: nextMeasurementDate?.measurementDate,
          baseDate: nextMeasurementDate?.measurementDate,
        })
      );
    }
  }, [measurementDate]);

  useEffect(() => {
    if (isSchedulesLoading) return;
    if (schedules === null) {
      setIsGanttInitialRenderState(true);
    }
  }, [isSchedulesLoading]);
  useEffect(() => {
    //初回描画時、全てのAccordionを開く
    if (constructionZones.length > 0) {
      setOpenConstructionZoneIds(constructionZones.map((constructionZone) => constructionZone.id));
    }
  }, []);

  const { ganttTasks } = useMemo(
    () =>
      convertScheduleConstructionZonesToGanttTask({
        schedule: schedules,
        site,
      }),
    //todo mutation検知して、再描画
    [site, schedules]
  );

  const allTasks = getAllTasks(constructionZones);

  const handleClickCross = (taskId: number, date: string) => {
    const newGanttSelection = convertFrappeGanttCrossToGanttSelection(
      { taskId, date },
      ganttSelection,
      asBuiltDataFiles,
      allTasks
    );

    setGanttSelectionState(newGanttSelection);
  };
  const handleClickDate = (date: string) => {
    const newGanttSelection = convertFrappeGanttDateToGanttSelection(
      date,
      ganttSelection,
      asBuiltDataFiles
    );
    setGanttSelectionState(newGanttSelection);
  };

  const findTaskById = (taskId: number) => allTasks.find((task) => task.id === taskId);
  const [isLoadingCreateTask, setIsLoadingCreateTask] = useState(false);
  const { mutate } = useSWRConfig();
  const onSubmitCreateTask = async (data: TaskFormSchemaType, constructionZoneId: number) => {
    // data.constructionZoneIdがnullであり、そのままAPIを叩くとエラーになるため、
    // constructionZoneIdを正しい値に書き換えたオブジェクト(task)を作成
    const task = { ...data, constructionZoneId };

    try {
      setIsLoadingCreateTask(true);
      if (!siteId) {
        throw new Error('siteId is undefined');
      }
      //TODO Any型を直す
      const result = await createTask({ task: task as any, siteId, constructionZoneId });
      if (!result) throw new Error('result is undefined');
      notifications.show({
        title: '成功',
        message: '作業を追加しました',
        //   icon: <Icon icon="ant-design:check-outlined" />,
      });
      mutate([SWR_KEYS.CONSTRUCTION_ZONE_LIST, siteId]); // 工区一覧を再取得
      //GanttのRefresh
      mutate([SWR_KEYS.SCHEDULE, siteId]);
    } catch (e) {
      notifications.show({
        title: '失敗',
        message: '作業を追加できませんでした',
        // icon: <Icon icon="ant-design:close-outlined" />,
        color: 'red',
      });
    } finally {
      setIsLoadingCreateTask(false);
    }
  };

  const transformedGanttSelection = useMemo(
    () => transformGanttSelection(ganttSelection, dataFiles, allTasks),
    [ganttSelection, dataFiles, allTasks]
  );
  const selectedTasksWithSoil = useMemo(() => {
    if (!transformedGanttSelection || !schedulesWithQuery?.constructionZones) return [];
    return getSelectedTasksWithSoil(
      transformedGanttSelection,
      schedulesWithQuery?.constructionZones
    );
  }, [transformedGanttSelection, schedulesWithQuery?.constructionZones]);

  const highLightOptions = useMemo(
    () => convertGanttSelectionToFrappeGantt(ganttSelection, asBuiltDataFiles, allTasks),
    [ganttSelection, asBuiltDataFiles, allTasks]
  );
  const setDisplayedDataFile = useSetRecoilState(displayedDataFileState);
  const hasInitializedLatestDate = useRef(false);
  //asBuiltDataFilesの最新のデータを選択状態にする
  useEffect(() => {
    // isGanttInitialRenderがtrueの間は待機
    if (isGanttInitialRender) return;
    if (!asBuiltDataFiles || asBuiltDataFiles.length === 0) return;
    if (hasInitializedLatestDate.current) return; // 既に初期化済みの場合はスキップ

    // 日付でソートして最新のデータを取得
    const sortedAsBuiltDataFiles = [...asBuiltDataFiles].sort((a, b) => {
      // 日付がない場合は、最後に表示
      if (!a.measurementDate) return 1;
      if (!b.measurementDate) return -1;
      const dateA = dayjs(a.measurementDate);
      const dateB = dayjs(b.measurementDate);
      return dateB.isAfter(dateA) ? 1 : -1;
    });
    const latestAsBuiltDataFile = sortedAsBuiltDataFiles[0];
    if (!latestAsBuiltDataFile) return;
    const newGanttSelection = {
      date: {
        dataFile: latestAsBuiltDataFile,
        selectedDateTime: dayjs().unix(),
      },
    };
    setGanttSelectionState((prev) => ({
      ...prev,
      ...newGanttSelection,
    }));

    // 最新日付選択時に、関連する設計データの0番目を自動チェック
    const currentTransformedSelection = transformGanttSelection(
      newGanttSelection,
      dataFiles,
      allTasks
    );
    if (currentTransformedSelection.designDataFiles?.length > 0) {
      const firstDesignDataFile = currentTransformedSelection.designDataFiles[0];
      setDisplayedDataFile((current) => {
        const isFirstDesignDataDisplayed = current.designData.some(
          (d) => d.data.id === firstDesignDataFile.id
        );
        if (isFirstDesignDataDisplayed) return current;
        return {
          ...current,
          designData: [
            {
              isExist: true,
              data: firstDesignDataFile,
            },
          ],
        };
      });
    }
    hasInitializedLatestDate.current = true;
  }, [asBuiltDataFiles, isGanttInitialRender, dataFiles, allTasks]);
  const [optionSetting] = useRecoilState(optionSettingState);

  const { meshSize } = optionSetting;
  const { data: soilAmountTasksData, isLoading: soilAmountTasksIsLoading } = useSWR(
    [
      SWR_KEYS.SOIL_AMOUNT_TASKS_BY_DATE,
      //データファイルID
      transformedGanttSelection.asBuiltDataFile?.id,
      //メッシュサイズ
      meshSize,
      siteId,
      transformedGanttSelection.selectionType,
    ],
    (value) => fetchSoilAmountDataByDate(value),
    {
      revalidateOnFocus: false,
    }
  );
  const { data: soilAmountTasksDataByTask, isLoading: soilAmountTasksByTaskIsLoading } = useSWR(
    [
      SWR_KEYS.SOIL_AMOUNT_TASKS_BY_TASK,
      //作業ID
      transformedGanttSelection.task?.id,
      //データファイルID
      transformedGanttSelection.asBuiltDataFile?.id,
      //メッシュサイズ
      meshSize,
      siteId,
      transformedGanttSelection.selectionType,
    ],
    (value) => fetchSoilAmountDataByTask(value),
    {
      revalidateOnFocus: false,
    }
  );
  //土量結果を集約して返却する
  const soilData = (() => {
    switch (transformedGanttSelection.selectionType) {
      case GanttSelectionType.date:
        return soilAmountTasksData;
      case GanttSelectionType.cross:
        return soilAmountTasksDataByTask;
      default:
        return null;
    }
  })();
  const isSoilDataFetching = (() => {
    switch (transformedGanttSelection.selectionType) {
      case GanttSelectionType.date:
        return soilAmountTasksIsLoading;
      case GanttSelectionType.cross:
        return soilAmountTasksByTaskIsLoading;
      default:
        return false;
    }
  })();
  const isSchedulePage = useMemo(() => router.pathname.includes('schedule'), [router.pathname]);

  return {
    ganttTasks,

    constructionZones,
    dataFiles,
    asBuiltDataFiles,
    ganttSelection,
    setGanttSelectionState,
    scheduleSize,
    setScheduleSize,
    handleClickCross,
    handleClickDate,
    findTaskById,
    isValidatingSchedulesWithQuery,
    isLoadingCreateTask,
    scheduleDetailConstructionZones: schedules?.constructionZones,
    onSubmitCreateTask,
    site,
    allTasks,
    isValidationSchedules,
    frappeGanttInstance,
    setFrappeGanttInstance,
    frappeGanttViewMode,
    setFrappeGanttViewMode,
    openConstructionZoneIds,
    setOpenConstructionZoneIds,
    transformedGanttSelection,
    highLightOptions,
    soilData,
    isSoilDataFetching,
    meshSize,

    schedules,
    selectedTasksWithSoil,
    setIsGanttInitialRenderState,
    isGanttInitialRender,
    resetFrappeGanttInstance,
    resetFrappeGanttViewModeState,
    resetGanttSelectionState,
    resetScheduleSizeState,
    resetIsGanttInitialRenderState,
    isSchedulePage,
    user,
    schedulesWithQuery,
  };
};
